/**
 * Mobile attachment upload.
 *
 * Mobile could open attachments (AttachmentLinkClick is wired into the note
 * editor) but had no way to add one — the upload entry points lived only in
 * the desktop markdown toolbar. This module provides the mobile equivalent:
 * a camera capture and a file picker, both feeding the same
 * validate -> uploadAttachment -> insert pipeline the desktop uses.
 *
 * The two <input type="file"> elements live on document.body rather than in
 * the mobile container. The shell rebuilds its container with innerHTML, which
 * would destroy an in-flight picker (and on some mobile browsers cancels the
 * camera intent). Keeping them outside means one pair of inputs for the
 * session, bound once.
 *
 * Insertion has two targets, matching the note editor's two modes:
 *   - collab docs: the live TipTap editor (image node, or a markdown link for
 *     non-images) — the ySyncPlugin propagates it like any other edit.
 *   - unmigrated docs: the plain textarea, where we splice markdown at the
 *     caret and fire `input` so the draft/stash machinery sees the change.
 */

import {
  loadAttachmentDek,
  populateAttachmentCache,
  uploadAttachment,
  validateFile,
} from "../../../engine/services/attachment-service.js";
import { getMobileNoteEditor } from "./note-editor-mount.js";
import { t } from "../../../shared/i18n/index.js";

// Mirrors ALLOWED_EXTENSIONS in attachment-service.js (not exported there).
// Used only as a picker hint — validateFile remains the real gate.
const ACCEPT_ATTR = [
  "image/*", "audio/*", "application/pdf",
  ".md", ".txt", ".csv", ".json",
  ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx",
].join(",");

let cameraInput = null;
let fileInput = null;
let deps = null;

/**
 * @param {Object} cfg
 * @param {() => string|null} cfg.getDocId  doc the attachment belongs to
 * @param {(msg: string, opts?: Object) => void} cfg.showToast
 * @param {() => void} cfg.scheduleRender
 */
export function configureMobileAttachments(cfg) {
  deps = cfg;
}

function buildInput({ capture }) {
  const input = document.createElement("input");
  input.type = "file";
  input.hidden = true;
  input.accept = capture ? "image/*" : ACCEPT_ATTR;
  if (capture) {
    // Hints the OS to open the rear camera directly instead of the gallery.
    // Ignored on desktop browsers, which fall back to a normal file dialog.
    input.setAttribute("capture", "environment");
  } else {
    input.multiple = true;
  }
  input.addEventListener("change", async () => {
    const files = Array.from(input.files || []);
    // Reset immediately so picking the same file twice still fires `change`.
    input.value = "";
    if (files.length) await uploadAndInsert(files);
  });
  document.body.appendChild(input);
  return input;
}

/** Open the camera (or the gallery if the device has no camera). */
export function pickAttachmentPhoto() {
  if (!cameraInput) cameraInput = buildInput({ capture: true });
  cameraInput.click();
}

/** Open the generic file picker. */
export function pickAttachmentFile() {
  if (!fileInput) fileInput = buildInput({ capture: false });
  fileInput.click();
}

/**
 * Validate, upload and insert each file, one at a time so a failure part-way
 * through still leaves the earlier ones inserted (and reported).
 */
async function uploadAndInsert(files) {
  const docId = deps?.getDocId?.();
  if (!docId) {
    deps?.showToast?.(t("mobile.attach.openNote"), { type: "error" });
    return;
  }

  // The DEK may not be resolved yet for a doc opened this session; loadAttachmentDek
  // falls back to fetching + unwrapping it.
  let dek = null;
  try {
    dek = await loadAttachmentDek(docId);
  } catch (err) {
    console.error("[MobileAttach] DEK load failed:", err);
  }
  if (!dek) {
    deps?.showToast?.(
      t("mobile.attach.noKey"),
      { type: "error" },
    );
    return;
  }

  let ok = 0;
  for (const file of files) {
    const check = validateFile(file);
    if (!check.valid) {
      deps?.showToast?.(check.error, { type: "error" });
      continue;
    }
    deps?.showToast?.(
      files.length > 1 ? t("mobile.attach.uploadingFile", { name: file.name }) : t("vault.transfer.uploading"),
      { type: "info" },
    );
    try {
      const { attachmentId, filename, mimeType } = await uploadAttachment(docId, file, dek);
      // Pre-seed the cache so the inserted reference renders/opens immediately
      // instead of round-tripping to the server.
      populateAttachmentCache(docId, attachmentId, { blob: file, mimeType, filename });
      insertReference({ docId, attachmentId, filename, mimeType });
      ok++;
    } catch (err) {
      console.error("[MobileAttach] Upload failed:", err);
      deps?.showToast?.(err?.message || t("vault.attachment.uploadFailed"), { type: "error" });
    }
  }

  if (ok > 0) {
    deps?.showToast?.(
      t("mobile.attach.added", { count: ok }),
      { type: "success" },
    );
    // The textarea path mutates a DOM value the shell does not own; repaint so
    // the draft and any dependent UI stay in step.
    deps?.scheduleRender?.();
  }
}

/** Insert the `attachment:` reference into whichever editor is live. */
function insertReference({ docId, attachmentId, filename, mimeType }) {
  const url = `attachment:${docId}/${attachmentId}`;
  const isImage = String(mimeType || "").startsWith("image/");

  const editor = getMobileNoteEditor();
  if (editor && !editor.isDestroyed) {
    if (isImage) {
      editor.chain().focus().setImage({ src: url, alt: filename }).run();
    } else {
      editor.chain().focus().insertContent(`[${filename}](${url})`).run();
    }
    return;
  }

  // Textarea fallback (docs not yet migrated to collab note fragments).
  const ta = document.getElementById("mobile-note-content");
  if (!ta) {
    deps?.showToast?.(t("mobile.attach.noNote"), { type: "warning" });
    return;
  }
  const md = isImage ? `![${filename}](${url})` : `[${filename}](${url})`;
  const start = ta.selectionStart ?? ta.value.length;
  const end = ta.selectionEnd ?? start;
  ta.value = `${ta.value.slice(0, start)}${md}${ta.value.slice(end)}`;
  const caret = start + md.length;
  ta.setSelectionRange(caret, caret);
  // Let the shell's input handler snapshot this into the note draft.
  ta.dispatchEvent(new Event("input", { bubbles: true }));
}

/** Remove the body-level inputs (called from the shell's destroy path). */
export function destroyMobileAttachments() {
  for (const el of [cameraInput, fileInput]) {
    try { el?.remove(); } catch { /* already detached */ }
  }
  cameraInput = null;
  fileInput = null;
  deps = null;
}
